/**
 * An athlete or manager inviting the brand onto a deal they started. Lives in the Campaigns hub's
 * Incoming tab.
 */
import React from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Loader2, ArrowRight } from 'lucide-react'
import { toast } from 'sonner'
import { useDeclineInvitationMutation, MarketerInvitation } from '@/redux/marketerApi'

export const InvitationCard = ({ invitation }: { invitation: MarketerInvitation }) => {
  const navigate = useNavigate()
  const [declineInvitation, { isLoading: isDeclining }] = useDeclineInvitationMutation()

  const inviterName = invitation.inviter?.name || 'A collaborator'
  const collab = invitation.collaboration

  const handleDecline = async () => {
    try {
      await declineInvitation(invitation.token).unwrap()
      toast.success('Invitation declined')
    } catch (err) {
      toast.error('Failed to decline invitation')
    }
  }

  return (
    <div className="rounded-2xl border border-foreground/10 bg-card p-5 md:p-6">
      <div className="flex flex-col gap-5 md:flex-row md:items-start md:justify-between">
        <div className="flex gap-4">
          <Avatar className="h-11 w-11 shrink-0">
            <AvatarImage src={invitation.inviter?.avatar || undefined} alt={inviterName} />
            <AvatarFallback>{inviterName.charAt(0).toUpperCase()}</AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <p className="text-base font-semibold text-foreground">
              {inviterName} invited you to collaborate
            </p>
            {collab?.title && <p className="mt-0.5 text-sm text-gray-700">{collab.title}</p>}
            {invitation.message && (
              <p className="mt-2 text-sm italic text-gray-600">
                &ldquo;{invitation.message}&rdquo;
              </p>
            )}

            <div className="mt-3 flex flex-wrap gap-2 text-xs">
              {collab?.athleteName && (
                <span className="rounded-full border border-foreground/10 bg-card px-3 py-1 text-gray-700">
                  Athlete: {collab.athleteName}
                </span>
              )}
              {collab?.brandName && (
                <span className="rounded-full border border-foreground/10 bg-card px-3 py-1 text-gray-700">
                  Brand: {collab.brandName}
                </span>
              )}
              {collab?.offerDetails?.deliverables && (
                <span className="rounded-full border border-foreground/10 bg-card px-3 py-1 text-gray-700">
                  {collab.offerDetails.deliverables}
                </span>
              )}
              {collab?.paymentAmount != null && (
                <span className="rounded-full border border-[#C0FD72]/30 bg-[#C0FD72]/10 px-3 py-1 font-medium text-[#3F7A0A]">
                  ${collab.paymentAmount}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex shrink-0 gap-2 md:flex-col md:items-stretch">
          <Button
            className="bg-[#C0FD72] font-semibold text-black hover:bg-[#b0ef62]"
            onClick={() => navigate(`/invitation/${invitation.token}`)}
          >
            Review &amp; Accept
            <ArrowRight className="ml-1.5 h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            className="border-foreground/15 bg-transparent text-foreground hover:bg-foreground/5"
            onClick={handleDecline}
            disabled={isDeclining}
          >
            {isDeclining ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Decline'}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default InvitationCard
